import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import { Link } from 'react-router-dom';
import ProofBasis, { ProofMark } from '../components/ProofBasis';
import { HOW_WE_MEASURE_PATH, MEDIAN_FIRST_REPLY, ORGANISATIONS, PROOF_PERIOD } from '../data/proofPoints';
import { apiEndpoint } from '../config';
import { trackLeadConversion, trackBookingClick, trackContactClick } from '../utils/analytics';
import { bookingClickHandler } from '../utils/bookingLink';
import { getAttribution } from '../utils/leadSource';
import { useTurnstile } from '../components/LazyTurnstileProvider';
import {
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  ClockIcon,
  ChatBubbleBottomCenterTextIcon,
  RocketLaunchIcon,
  SparklesIcon,
  CheckCircleIcon,
  BoltIcon,
  GlobeAltIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';
import SEO from '../components/SEO';
import { PageHeroTitle } from '../styles/typography';

// Turnstile action and the server's form_type for this form (see _CONTACT_FORM_TYPES in app.py).
const FORM_TYPE = 'contact_page';

// Animations
const gradientMove = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Styled Components
const PageWrapper = styled.div`
  padding: var(--spacing-section-mobile) 0;
  background: linear-gradient(180deg, 
    var(--color-primary-light) 0%, 
    var(--color-primary-base) 50%,
    var(--color-primary-light) 100%);
  min-height: 100vh;
  position: relative;
  overflow: hidden;

  @media (min-width: 768px) {
    padding: var(--spacing-section) 0;
  }

  // Background tech grid
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
      linear-gradient(rgba(11,152,206,0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(11,152,206,0.03) 1px, transparent 1px);
    background-size: 50px 50px;
    pointer-events: none;
    animation: ${pulse} 8s ease-in-out infinite;
  }
`;

const HeroSection = styled.section`
  text-align: center;
  margin-bottom: var(--spacing-4xl);
  position: relative;
  animation: ${slideUp} 0.8s ease-out;
`;

const Title = PageHeroTitle;

const Subtitle = styled.p`
  font-size: var(--fs-xl);
  color: var(--text-secondary);
  max-width: 700px;
  margin: 0 auto var(--spacing-xl);
  line-height: var(--lh-relaxed);
`;

const StatusBar = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-lg);
  padding: var(--spacing-sm) var(--spacing-lg);
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border-radius: 999px;
  box-shadow: var(--shadow-md);
  border: 1px solid rgba(11,152,206,0.1);
  margin-top: var(--spacing-lg);

  @media (max-width: 640px) {
    display: flex;
    width: 100%;
    border-radius: 24px;
    padding: var(--spacing-sm) var(--spacing-md);
    justify-content: space-evenly;
    gap: var(--spacing-xs);
  }
`;

const StatusItem = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  
  .icon {
    width: 16px;
    height: 16px;
    color: var(--color-accent-primary);
  }
  
  .text {
    font-size: var(--fs-sm);
    color: var(--text-secondary);
    font-weight: var(--font-weight-medium);
  }
  
  .value {
    color: var(--color-accent-primary);
    font-weight: var(--font-weight-bold);
  }

  .text a {
    display: block;
    font-size: var(--fs-xs);
    color: var(--text-secondary);
    text-decoration: underline;
  }

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
    flex: 1;

    .value {
      display: block;
    }
  }
`;

const LiveIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  
  .dot {
    width: 8px;
    height: 8px;
    background: var(--color-success);
    border-radius: 50%;
    animation: ${pulse} 2s ease-in-out infinite;
  }
  
  .text {
    font-size: var(--fs-xs);
    color: var(--color-success);
    font-weight: var(--font-weight-medium);
  }

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
    flex: 1;
  }
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--spacing-3xl);
  position: relative;
  z-index: 2;
  /* Columns size to their own content — prevents a tall info column from
     stretching the form card into empty space */
  align-items: start;
  /* Narrow-viewport guard (round 10 F3): grid items default to min-width:auto,
     so a wide child (service tags, quick actions) can force the track — and
     the FormCard — past 390px. minmax(0,1fr) lets the track shrink. */
  grid-template-columns: minmax(0, 1fr);

  @media (min-width: 1024px) {
    grid-template-columns: 1.5fr 1fr;
  }
`;

const FormCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: var(--spacing-3xl);
  box-shadow: 0 20px 60px rgba(0,0,0,0.08);
  border: 1px solid rgba(11,152,206,0.1);
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: var(--spacing-xl) var(--spacing-md);
    /* 390px audit (round 10 F3): the card is full-bleed inside .container's
       16px gutters, so keep the padding symmetric — asymmetric inline padding
       pushed the card box past the viewport edge. */
    box-sizing: border-box;
    width: 100%;
    max-width: 100%;
  }
  animation: ${slideUp} 0.8s ease-out 0.2s both;

  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(135deg, 
      var(--color-accent-primary), 
      var(--color-accent-secondary),
      var(--color-accent-primary));
    background-size: 300% 300%;
    animation: ${gradientMove} 8s ease infinite;
    border-radius: 24px;
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: -1;
  }

  &:hover::before {
    opacity: 0.3;
  }
`;

const FormHeader = styled.div`
  margin-bottom: var(--spacing-2xl);
  
  h2 {
    font-size: var(--fs-2xl);
    color: var(--text-primary);
    margin-bottom: var(--spacing-sm);
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    
    svg {
      width: 28px;
      height: 28px;
      color: var(--color-accent-primary);
    }
  }
  
  p {
    color: var(--text-secondary);
    font-size: var(--fs-md);
  }

  @media (max-width: 768px) {
    h2 {
      text-align: center;
      justify-content: center;

      svg {
        display: none;
      }
    }
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xl);
`;

const InputGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--spacing-lg);
  
  @media (min-width: 640px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const InputGroup = styled.div<{ $fullWidth?: boolean }>`
  grid-column: ${props => props.$fullWidth ? '1 / -1' : 'span 1'};
  position: relative;
  /* Narrow-viewport guard (round 10 F3): with the MainGrid minmax fix, children
     must still not impose their own minimums (long placeholders, tags). */
  min-width: 0;
`;

const Label = styled.label`
  display: block;
  font-size: var(--fs-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
  margin-bottom: var(--spacing-xs);
  transition: color 0.3s ease;
  
  &.required::after {
    content: ' *';
    color: var(--color-accent-primary);
  }
`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: var(--spacing-md);
  font-size: var(--fs-md);
  border: 2px solid var(--border-light);
  border-radius: 12px;
  background: var(--color-primary-light);
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: var(--color-accent-primary);
    background: white;
    box-shadow: 0 0 0 4px rgba(11,152,206,0.1);
  }
  
  &:hover {
    border-color: rgba(11,152,206,0.3);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  padding: var(--spacing-md);
  font-size: var(--fs-md);
  border: 2px solid var(--border-light);
  border-radius: 12px;
  background: var(--color-primary-light);
  min-height: 120px;
  resize: vertical;
  transition: all 0.3s ease;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: var(--color-accent-primary);
    background: white;
    box-shadow: 0 0 0 4px rgba(11,152,206,0.1);
  }
  
  &:hover {
    border-color: rgba(11,152,206,0.3);
  }
`;

const ServiceTags = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-md);
`;

const ServiceTag = styled.button<{ $selected?: boolean }>`
  padding: var(--spacing-xs) var(--spacing-md);
  border-radius: 999px;
  border: 2px solid ${props => props.$selected ? 'var(--color-accent-primary)' : 'var(--border-light)'};
  background: ${props => props.$selected ? 'var(--color-accent-primary)' : 'white'};
  color: ${props => props.$selected ? 'white' : 'var(--text-secondary)'};
  font-size: var(--fs-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-sm);
  }
  
  svg {
    width: 14px;
    height: 14px;
    margin-right: 4px;
    vertical-align: middle;
  }
`;

const SubmitButton = styled.button`
  padding: var(--spacing-md) var(--spacing-2xl);
  background: linear-gradient(135deg, var(--color-accent-primary-fill), var(--color-accent-secondary));
  color: white;
  border: none;
  border-radius: 12px;
  font-size: var(--fs-md);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease, opacity 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
    transform: translateX(-100%);
    transition: transform 0.5s ease;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(11,152,206,0.3);

    &::before {
      transform: translateX(100%);
    }
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  svg {
    width: 20px;
    height: 20px;
    margin-left: 8px;
    vertical-align: middle;
  }

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    /* 390px audit (round 10 F3): full-width submit target — easier thumb hit,
       no clipped trailing icon. white-space:nowrap stays so the label never
       wraps mid-word. */
    width: 100%;
    box-sizing: border-box;
    white-space: nowrap;
    padding: var(--spacing-md) var(--spacing-xl);

    svg {
      margin-left: 0;
    }
  }
`;

const InfoCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xl);
  animation: ${slideUp} 0.8s ease-out 0.4s both;
`;

const InfoCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: var(--spacing-xl);
  border: 1px solid rgba(11,152,206,0.1);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 40px rgba(0,0,0,0.08);
    border-color: var(--color-accent-primary);
  }
  
  h3 {
    font-size: var(--fs-lg);
    color: var(--text-primary);
    margin-bottom: var(--spacing-md);
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    
    svg {
      width: 24px;
      height: 24px;
      color: var(--color-accent-primary);
    }
  }
  
  .content {
    color: var(--text-secondary);
    line-height: var(--lh-relaxed);
    
    a {
      color: var(--color-accent-primary);
      text-decoration: none;
      font-weight: var(--font-weight-medium);
      transition: all 0.2s ease;
      
      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

const QuickActions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-sm);
  margin-top: var(--spacing-lg);
`;

const QuickAction = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm);
  background: var(--color-primary-light);
  border-radius: 12px;
  color: var(--text-primary);
  text-decoration: none;
  font-size: var(--fs-sm);
  font-weight: var(--font-weight-medium);
  transition: all 0.2s ease;
  border: 1px solid transparent;
  
  &:hover {
    background: white;
    border-color: var(--color-accent-primary);
    transform: translateY(-2px);
    box-shadow: var(--shadow-sm);
  }
  
  svg {
    width: 16px;
    height: 16px;
    color: var(--color-accent-primary);
  }
`;

const FloatingIcon = styled.div<{ $delay?: number }>`
  position: absolute;
  opacity: 0.05;
  animation: ${float} 6s ease-in-out infinite;
  animation-delay: ${props => props.$delay || 0}s;
  pointer-events: none;
  
  svg {
    width: 80px;
    height: 80px;
    color: var(--color-accent-primary);
  }
`;

const Alert = styled.div<{ $type: 'success' | 'error' }>`
  padding: var(--spacing-md);
  border-radius: 12px;
  margin-bottom: var(--spacing-lg);
  background: ${props => props.$type === 'success' 
    ? 'linear-gradient(135deg, rgba(76, 175, 80, 0.1), rgba(76, 175, 80, 0.05))' 
    : 'linear-gradient(135deg, rgba(244, 67, 54, 0.1), rgba(244, 67, 54, 0.05))'};
  color: ${props => props.$type === 'success' ? 'var(--color-success)' : 'var(--color-error)'};
  border: 1px solid ${props => props.$type === 'success' 
    ? 'rgba(76, 175, 80, 0.3)' 
    : 'rgba(244, 67, 54, 0.3)'};
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  animation: ${slideUp} 0.3s ease-out;
  
  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
`;

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
    services: [] as string[],
    page_source: ''
  });

  // Capture page source URL on mount
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      page_source: window.location.href
    }));
  }, []);
  
  const { getToken, loadTurnstile } = useTurnstile();
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error', message: string } | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const services = [
    { name: 'Transformative AI Solutions', icon: <SparklesIcon /> },
    { name: 'Strategic IT Projects', icon: <RocketLaunchIcon /> },
    { name: 'Intelligent Managed IT', icon: <BoltIcon /> },
    { name: 'AI-Augmented Helpdesk Support', icon: <ChatBubbleBottomCenterTextIcon /> }
  ];

  const handleServiceToggle = (service: string) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setAlert(null);

    try {
      const turnstileToken = await getToken(FORM_TYPE);
      const response = await axios.post(apiEndpoint('/api/contact'), {
        ...formData,
        form_type: FORM_TYPE,
        services: formData.services.join(', '),
        attribution: getAttribution(),
        turnstileToken
      });
      
      if (response.data.success) {
        // Track lead generation in Google Analytics
        trackLeadConversion('contact_page');

        setAlert({
          type: 'success',
          message: 'Message sent successfully! We will be in touch shortly during business hours.'
        });
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          message: '',
          services: [],
          page_source: window.location.href
        });
      }
    } catch (error: any) {
      setAlert({
        type: 'error',
        message: error.response?.data?.error || 'We could not send your request. Please try again, or call (403) 245-9429.'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', { 
      timeZone: 'America/Denver',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true 
    });
  };

  return (
    <PageWrapper>
      <SEO title="Contact Davinci AI Solutions | Calgary AI Experts" description="Contact Davinci AI Solutions in Calgary, Alberta. Speak with our AI and IT experts to discuss your project. Clear response-time targets." path="/contact" />
      <FloatingIcon style={{ top: '10%', left: '5%' }} $delay={0}>
        <RocketLaunchIcon />
      </FloatingIcon>
      <FloatingIcon style={{ top: '20%', right: '8%' }} $delay={2}>
        <SparklesIcon />
      </FloatingIcon>
      <FloatingIcon style={{ bottom: '15%', left: '10%' }} $delay={4}>
        <GlobeAltIcon />
      </FloatingIcon>
      
      <div className="container">
        <HeroSection>
          <Title>Talk to the Team That Builds It</Title>
          <Subtitle>
            Let's connect and explore how a partnership with us can help unlock your team's potential.
          </Subtitle>
          <StatusBar>
            <LiveIndicator>
              <div className="dot" />
              <span className="text">Systems Online</span>
            </LiveIndicator>
            <StatusItem>
              <ClockIcon className="icon" />
              <span className="text">Calgary Time: <span className="value">{formatTime(currentTime)}</span></span>
            </StatusItem>
            <StatusItem>
              <BoltIcon className="icon" />
              <span className="text">Median first reply, {PROOF_PERIOD}: <span className="value">{MEDIAN_FIRST_REPLY.value}</span><Link to={HOW_WE_MEASURE_PATH}>How we measure</Link></span>
            </StatusItem>
          </StatusBar>
        </HeroSection>

        <MainGrid>
          <FormCard>
            <FormHeader>
              <h2>
                <ChatBubbleBottomCenterTextIcon />
                Tell us what you're trying to build
              </h2>
              <p>Tell us about your current workflows, and we'll provide an assessment of how AI can improve your team's performance.</p>
            </FormHeader>

            {alert && (
              <Alert $type={alert.type}>
                {alert.type === 'success' ? <CheckCircleIcon /> : null}
                {alert.message}
              </Alert>
            )}

            <Form data-clarity-mask="True" onSubmit={handleSubmit} onFocus={() => loadTurnstile()}>
              <InputGrid>
                <InputGroup>
                  <Label htmlFor="name" className="required">Your Name</Label>
                  <Input
                    type="text"
                    id="name"
                    name="name"
                    placeholder="John Smith"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </InputGroup>

                <InputGroup>
                  <Label htmlFor="email" className="required">Email Address</Label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="john@company.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </InputGroup>

                <InputGroup>
                  <Label htmlFor="company">Company Name</Label>
                  <Input
                    type="text"
                    id="company"
                    name="company"
                    placeholder="Acme Corp"
                    value={formData.company}
                    onChange={handleChange}
                  />
                </InputGroup>

                <InputGroup>
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    type="tel"
                    id="phone"
                    name="phone"
                    placeholder="(403) 123-4567"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </InputGroup>
              </InputGrid>

              <InputGroup $fullWidth>
                <Label>I'm interested in:</Label>
                <ServiceTags>
                  {services.map(service => (
                    <ServiceTag
                      key={service.name}
                      type="button"
                      $selected={formData.services.includes(service.name)}
                      onClick={() => handleServiceToggle(service.name)}
                    >
                      {service.icon}
                      {service.name}
                    </ServiceTag>
                  ))}
                </ServiceTags>
              </InputGroup>

              <InputGroup $fullWidth>
                <Label htmlFor="message" className="required">Your Message</Label>
                <TextArea
                  id="message"
                  name="message"
                  placeholder="Describe your current workflows and team challenges. What repetitive tasks are slowing your team down?"
                  value={formData.message}
                  onChange={handleChange}
                  required
                />
              </InputGroup>

              <SubmitButton type="submit" disabled={loading}>
                {loading ? 'Sending Assessment Request...' : 'Get My Assessment'}
                <RocketLaunchIcon />
              </SubmitButton>
            </Form>
          </FormCard>

          <InfoCards>
            <InfoCard>
              <h3>
                <BoltIcon />
                Direct Connect
              </h3>
              <div className="content">
                <p>Need immediate assistance? Reach out directly — we reply the same business day.</p>
                <QuickActions>
                  <QuickAction href="tel:+14032459429" onClick={() => trackContactClick('phone', 'contact_page')}>
                    <PhoneIcon />
                    Call Now
                  </QuickAction>
                  <QuickAction href="mailto:info@davincisolutions.ai" onClick={() => trackContactClick('email', 'contact_page')}>
                    <EnvelopeIcon />
                    Email Us
                  </QuickAction>
                  <QuickAction
                    href="/book"
                    onClick={bookingClickHandler('contact_quick_action')}
                  >
                    <CalendarDaysIcon />
                    Book a Call
                  </QuickAction>
                </QuickActions>
              </div>
            </InfoCard>

            <InfoCard>
              <h3>
                <MapPinIcon />
                Headquarters
              </h3>
              <div className="content">
                <p>
                  <strong>Calgary, Alberta, Canada</strong><br />
                  Remote support for businesses anywhere in Alberta, and on-site support in the Calgary area.
                </p>
                <p style={{ marginTop: 'var(--spacing-md)' }}>
                  <strong>Business Hours:</strong><br />
                  Monday - Friday: 8:00 AM - 6:00 PM MST<br />
                  <span style={{ color: 'var(--color-accent-primary)' }}>
                    After-hours on-call for managed clients
                  </span>
                </p>
              </div>
            </InfoCard>

            <InfoCard>
              <h3>
                <SparklesIcon />
                Why Alberta Businesses Work With Davinci
              </h3>
              <div className="content">
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  <li>Solutions validated in our own operations</li>
                  <li>Trusted by {ORGANISATIONS.value}<ProofMark /> organisations</li>
                  <li>Calgary-based since 2016</li>
                  <li>A core philosophy of employee augmentation, not replacement</li>
                  <li>A track record of proven AI implementations</li>
                </ul>
                <ProofBasis />
              </div>
            </InfoCard>

            <InfoCard>
              <h3>
                <CalendarDaysIcon />
                Prefer to Skip the Form?
              </h3>
              <div className="content">
                <p>Book 30 minutes to discuss your priorities, explore whether we’re a fit, and agree on a practical next step.</p>
                {/* Microsoft's new Bookings pages refuse iframe embedding
                    (frame-ancestors), so this opens the live calendar in a
                    new tab via the /book vanity redirect. */}
                <SubmitButton
                  as="a"
                  href="/book"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackBookingClick('contact_booking_card')}
                  style={{
                    textDecoration: 'none',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                >
                  Book an AI & IT Discovery Call
                </SubmitButton>
                <p style={{ marginTop: 'var(--spacing-sm)', fontSize: 'var(--fs-sm)' }}>
                  Pick any open slot — you'll get a Teams invite automatically.
                </p>
              </div>
            </InfoCard>
          </InfoCards>
        </MainGrid>
      </div>
    </PageWrapper>
  );
};

export default Contact;
